import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, Calendar, Building2, Ruler,
  Home, Store, Hotel, Landmark, HandshakeIcon, CheckCircle2, ArrowRight, ShieldCheck, Clock, ClipboardCheck, Sparkles,
} from "lucide-react";
import PageHero from "./PageHero";
import Seo from "../components/common/Seo";
import { Reveal, SectionEyebrow, ArrowIcon } from "../components/common/Section";
import { useI18n, STRINGS } from "../lib/i18n";
import { resolveImage } from "../components/admin/ImageUpload";
import api from "../lib/api";

const CATEGORIES = ["residential", "commercial", "infrastructure", "hospitality", "mixed-use", "industrial"];
const STATUSES = ["completed", "ongoing", "upcoming"];

const SERVICE_CARDS = [
  { key: "residential", Icon: Home,     image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80" },
  { key: "commercial",  Icon: Store,    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80" },
  { key: "hospitality", Icon: Hotel,    image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80" },
  { key: "public",      Icon: Landmark, image: "https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?w=1200&q=80" },
];

const LAND_DEAL_BENEFIT_ICONS = [ShieldCheck, Sparkles, Clock, ClipboardCheck, HandshakeIcon];

export const ProjectsListPage = () => {
  const { t, pick, lang } = useI18n();
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    setLoaded(false);
    const q = new URLSearchParams();
    if (category) q.append("category", category);
    if (status) q.append("status", status);
    api
      .get(`/public/projects?${q.toString()}`)
      .then((r) => setProjects(r.data))
      .catch(() => setProjects([]))
      .finally(() => setLoaded(true));
  }, [category, status]);

  const grouped = useMemo(() => projects, [projects]);
  const benefitList =
    STRINGS[lang]?.construction?.landDeal?.benefits ||
    STRINGS.tr.construction.landDeal.benefits ||
    [];

  return (
    <div data-testid="construction-page">
      <PageHero
        eyebrow={t("construction.heroEyebrow")}
        title={t("construction.heroTitle")}
        subtitle={t("construction.heroSubtitle")}
        image="https://images.unsplash.com/photo-1541971875076-8f970d573be6?w=1920&q=80"
      />

      {/* WHAT WE DO — 4 SERVICE PILLARS */}
      <section className="bg-white" data-testid="construction-services">
        <div className="container-x py-20 md:py-28">
          <div className="max-w-3xl">
            <SectionEyebrow>{t("construction.heroEyebrow")}</SectionEyebrow>
            <h2 className="display display-md mt-6 text-brand-navy">{t("construction.whatWeDo")}</h2>
            <p className="mt-6 text-brand-dark/80 leading-relaxed">{t("construction.whatWeDoIntro")}</p>
          </div>

          <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {SERVICE_CARDS.map((s, i) => {
              const Icon = s.Icon;
              return (
                <motion.article
                  key={s.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="group relative overflow-hidden bg-brand-navy text-white"
                  data-testid={`service-card-${s.key}`}
                >
                  <div className="aspect-[4/5] relative overflow-hidden">
                    <img
                      src={s.image}
                      alt={t(`construction.services.${s.key}.title`)}
                      className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/40 via-brand-navy/60 to-brand-navy/95" />
                    <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-7">
                      <span className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/25">
                        <Icon size={20} strokeWidth={1.5} />
                      </span>
                      <div>
                        <h3 className="font-serif text-2xl md:text-[1.75rem] leading-tight">
                          {t(`construction.services.${s.key}.title`)}
                        </h3>
                        <p className="mt-3 text-sm text-white/80 leading-relaxed">
                          {t(`construction.services.${s.key}.body`)}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* KAT KARSILIĞI İNŞAAT — HERO CTA BLOCK */}
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #101F4A 0%, #22346B 55%, #1A2A5A 100%)" }}
        data-testid="land-deal-section"
      >
        <div
          className="absolute inset-0 opacity-30 mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1590725175785-de025dbecc70?w=1920&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative container-x py-20 md:py-28 text-white grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-white/50" />
                <span className="text-[0.66rem] tracking-[0.32em] uppercase text-white/70 font-medium">
                  {t("construction.landDeal.eyebrow")}
                </span>
              </div>
              <h2 className="mt-6 font-serif leading-tight" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                {t("construction.landDeal.title")}
              </h2>
              <p className="mt-6 text-white/80 leading-relaxed text-base md:text-lg max-w-xl">
                {t("construction.landDeal.body")}
              </p>
              <Link
                to="/contact?topic=land"
                className="mt-8 inline-flex items-center gap-3 bg-white text-brand-navy hover:bg-brand-navy hover:text-white border border-white transition-colors px-7 py-4 text-[0.72rem] tracking-[0.28em] uppercase font-medium"
                data-testid="land-deal-cta"
              >
                {t("construction.landDeal.cta")}
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <ul className="space-y-4">
                {benefitList.map((b, i) => {
                  const Icon = LAND_DEAL_BENEFIT_ICONS[i] || CheckCircle2;
                  return (
                    <li
                      key={i}
                      className="flex items-start gap-4 bg-white/5 hover:bg-white/10 transition-colors border border-white/10 backdrop-blur-sm p-4 md:p-5"
                      data-testid={`land-deal-benefit-${i}`}
                    >
                      <span className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
                        <Icon size={16} strokeWidth={1.5} />
                      </span>
                      <span className="text-white/90 leading-relaxed pt-1.5">{b}</span>
                    </li>
                  );
                })}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* REFERENCE PROJECTS */}
      <section className="bg-white" data-testid="reference-projects">
        <div className="container-x py-20 md:py-28">
          <div className="max-w-3xl mb-10 md:mb-12">
            <SectionEyebrow>{t("common.projects")}</SectionEyebrow>
            <h2 className="display display-md mt-6 text-brand-navy">{t("construction.referenceProjects")}</h2>
            <p className="mt-5 text-brand-dark/80 leading-relaxed">{t("construction.referenceIntro")}</p>
          </div>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10 border-b border-black/10 pb-8">
            <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium">Filter</div>
            <div className="flex flex-wrap gap-3" data-testid="category-filters">
              <button
                type="button"
                onClick={() => setCategory("")}
                className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border transition-colors ${
                  category === "" ? "bg-brand-navy text-white border-brand-navy" : "border-black/15 text-brand-dark hover:border-brand-navy"
                }`}
                data-testid="filter-cat-all"
              >
                {t("common.allCategories")}
              </button>
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border transition-colors ${
                    category === c ? "bg-brand-navy text-white border-brand-navy" : "border-black/15 text-brand-dark hover:border-brand-navy"
                  }`}
                  data-testid={`filter-cat-${c}`}
                >
                  {t(`common.${c}`)}
                </button>
              ))}
            </div>
            <div className="md:ml-auto flex flex-wrap gap-3" data-testid="status-filters">
              <button
                type="button"
                onClick={() => setStatus("")}
                className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border transition-colors ${
                  status === "" ? "bg-brand-gold text-white border-brand-gold" : "border-black/15 text-brand-dark hover:border-brand-gold"
                }`}
                data-testid="filter-status-all"
              >
                {t("common.allStatuses")}
              </button>
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border transition-colors ${
                    status === s ? "bg-brand-gold text-white border-brand-gold" : "border-black/15 text-brand-dark hover:border-brand-gold"
                  }`}
                  data-testid={`filter-status-${s}`}
                >
                  {t(`common.${s}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {!loaded
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} data-testid={`project-tile-loading-${i}`}>
                    <div className="aspect-[4/3] bg-black/5 animate-pulse" />
                    <div className="mt-5 h-3 w-32 bg-black/5 animate-pulse" />
                    <div className="mt-2 h-6 w-3/4 bg-black/5 animate-pulse" />
                  </div>
                ))
              : grouped.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: (i % 6) * 0.06 }}
                data-testid={`project-tile-${p.slug}`}
              >
                <Link to={`/projects/${p.slug}`} className="zoom-parent block group">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={resolveImage(p.cover_image)} alt={pick(p.name, "")} className="w-full h-full object-cover zoom-img" loading="lazy" />
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-[0.62rem] tracking-[0.24em] uppercase text-brand-navy">
                      {t(`common.${p.status}`) || p.status}
                    </div>
                  </div>
                  <div className="mt-5">
                    <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium">
                      {pick(p.location, "")} · {t(`common.${p.category}`) || p.category}
                    </div>
                    <div className="mt-2 font-serif text-2xl text-brand-navy leading-tight">{pick(p.name, "")}</div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {loaded && grouped.length === 0 ? (
            <div className="py-24 text-center text-brand-medium" data-testid="projects-empty">
              No projects found.
            </div>
          ) : null}
        </div>
      </section>

      {/* FINAL CONTACT CTA */}
      <section className="bg-brand-light border-t border-black/5" data-testid="construction-cta">
        <div className="container-x py-16 md:py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-primary font-medium">
              {t("construction.heroEyebrow")}
            </div>
            <h3 className="mt-3 font-serif text-brand-navy leading-tight" style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)" }}>
              {t("construction.contactCta")}
            </h3>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-brand-navy hover:bg-brand-primary text-white px-8 py-4 text-[0.72rem] tracking-[0.28em] uppercase font-medium transition-colors"
            data-testid="construction-contact-cta"
          >
            {t("home.contactCta")}
            <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export const ProjectDetailPage = () => {
  const { slug } = useParams();
  const { t, pick } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/public/projects/${slug}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [slug]);

  if (!data) return <div className="container-x pt-40 pb-20">…</div>;
  const { project, related } = data;

  return (
    <div data-testid="project-detail-page">
      <Seo seo={project.seo} fallbackTitle={pick(project.name)} fallbackDescription={pick(project.description)} fallbackImage={project.cover_image} />
      <PageHero eyebrow={t(`common.${project.category}`) || project.category} title={project.name} subtitle={project.location} image={resolveImage(project.cover_image)} />
      <section className="bg-white">
        <div className="container-x py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <ul className="divide-y divide-black/10 border-t border-b border-black/10">
                <li className="flex items-center justify-between py-4">
                  <span className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} /> Location</span>
                  <span className="text-brand-dark">{pick(project.location, "")}</span>
                </li>
                <li className="flex items-center justify-between py-4">
                  <span className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium flex items-center gap-2"><Calendar size={14} strokeWidth={1.5} /> Year</span>
                  <span className="text-brand-dark">{project.year || "-"}</span>
                </li>
                <li className="flex items-center justify-between py-4">
                  <span className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium flex items-center gap-2"><Building2 size={14} strokeWidth={1.5} /> Client</span>
                  <span className="text-brand-dark">{project.client || "-"}</span>
                </li>
                <li className="flex items-center justify-between py-4">
                  <span className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium flex items-center gap-2"><Ruler size={14} strokeWidth={1.5} /> Area</span>
                  <span className="text-brand-dark">{project.area_sqm ? `${project.area_sqm.toLocaleString()} m²` : "-"}</span>
                </li>
                <li className="flex items-center justify-between py-4">
                  <span className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium">Status</span>
                  <span className="text-brand-gold uppercase tracking-[0.22em] text-xs">{t(`common.${project.status}`) || project.status}</span>
                </li>
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <SectionEyebrow>{t("nav.projects")}</SectionEyebrow>
              <h2 className="display display-md mt-6">{pick(project.name, "")}</h2>
              <p className="mt-6 text-brand-dark leading-relaxed text-lg">{pick(project.description, "")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {(project.gallery || []).length ? (
        <section className="bg-brand-light">
          <div className="container-x py-20 md:py-28">
            <SectionEyebrow>Gallery</SectionEyebrow>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {project.gallery.map((g, i) => (
                <div key={i} className="aspect-[4/3] overflow-hidden">
                  <img src={resolveImage(g)} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {related?.length ? (
        <section className="bg-white">
          <div className="container-x py-20 md:py-28">
            <SectionEyebrow>Related Projects</SectionEyebrow>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link key={r.id} to={`/projects/${r.slug}`} className="zoom-parent block group" data-testid={`related-project-${r.slug}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={r.cover_image} alt={pick(r.name, "")} className="w-full h-full object-cover zoom-img" loading="lazy" />
                  </div>
                  <div className="mt-4 font-serif text-xl text-brand-navy">{pick(r.name, "")}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};
